import React, { useState } from 'react';
import { XCircleIcon, MessageCircleIcon } from 'lucide-react';
import { Configuration } from '../utils/types';
interface CTABarProps {
  configuration: Configuration;
  showVat: boolean;
}
export function CTABar({
  configuration,
  showVat
}: CTABarProps) {
  const [showModal, setShowModal] = useState(false);
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' kr';
  };
  // Apply VAT if needed
  const vatRate = 0.25; // 25% VAT
  const totalPrice = showVat ? configuration.totalPrice * (1 + vatRate) : configuration.totalPrice;
  // Sample selected zones
  const selectedZones = [{
    id: 1,
    name: 'Stue'
  }, {
    id: 2,
    name: 'Køkken'
  }, {
    id: 3,
    name: 'Bad'
  }, {
    id: 4,
    name: 'Soveværelse'
  }];
  return <>
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-10" data-id="element-77">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3" data-id="element-78">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between" data-id="element-79">
            {/* Selected zones */}
            <div className="flex flex-wrap gap-2 mb-3 sm:mb-0" data-id="element-80">
              {selectedZones.map(zone => <div key={zone.id} className="bg-gray-100 px-2 py-1 rounded-md text-sm text-gray-700 flex items-center" data-id="element-81">
                  {zone.name}
                  <button className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-82">
                    <XCircleIcon className="h-4 w-4" data-id="element-83" />
                  </button>
                </div>)}
            </div>
            {/* Total price */}
            <div className="flex items-center mb-3 sm:mb-0" data-id="element-84">
              <div data-id="element-85">
                <div className="text-lg font-medium text-gray-900" data-id="element-86">
                  {formatCurrency(totalPrice)}
                </div>
                <div className="text-xs text-gray-500" data-id="element-87">
                  fra {configuration.monthlyPayment} kr/md
                </div>
              </div>
            </div>
            {/* CTA buttons */}
            <div className="flex items-center space-x-3 w-full sm:w-auto" data-id="element-88">
              <button className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-lg text-sm transition-colors flex-grow sm:flex-grow-0" data-id="element-89">
                Standardisér til Roth
              </button>
              <button className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-lg text-sm transition-colors flex items-center flex-grow sm:flex-grow-0" data-id="element-90">
                <MessageCircleIcon className="h-4 w-4 mr-1" data-id="element-91" />
                Stil spørgsmål
              </button>
              <button className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm transition-colors flex-grow sm:flex-grow-0" onClick={() => setShowModal(true)} data-id="element-92">
                Acceptér løsning
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Accept modal */}
      {showModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20 p-4" data-id="element-93">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-id="element-94">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-95">
              Acceptér løsning
            </h3>
            <div className="mb-4" data-id="element-96">
              <p className="text-sm text-gray-700 mb-4" data-id="element-97">
                Du er ved at acceptere den konfigurerede løsning til en samlet
                pris på {formatCurrency(totalPrice)}{' '}
                {showVat ? 'inkl. moms' : 'ekskl. moms'}.
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4" data-id="element-98">
                <div className="flex items-center mb-2" data-id="element-99">
                  <input type="checkbox" id="terms-check" className="h-4 w-4 text-red-500 border-gray-300 rounded focus:ring-red-500" data-id="element-100" />
                  <label htmlFor="terms-check" className="ml-2 text-sm text-gray-700" data-id="element-101">
                    Jeg accepterer vilkår og betingelser
                  </label>
                </div>
                <div className="flex items-center" data-id="element-102">
                  <input type="checkbox" id="data-check" className="h-4 w-4 text-red-500 border-gray-300 rounded focus:ring-red-500" data-id="element-103" />
                  <label htmlFor="data-check" className="ml-2 text-sm text-gray-700" data-id="element-104">
                    Jeg accepterer, at mine data behandles i henhold til
                    privatlivspolitikken
                  </label>
                </div>
              </div>
              <div className="mb-4" data-id="element-105">
                <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-106">
                  Underskrift (skriv dit navn)
                </label>
                <input type="text" id="signature" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="Skriv dit fulde navn her" data-id="element-107" />
              </div>
            </div>
            <div className="flex justify-end space-x-3" data-id="element-108">
              <button className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-lg text-sm transition-colors" onClick={() => setShowModal(false)} data-id="element-109">
                Annuller
              </button>
              <button className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm transition-colors" data-id="element-110">
                Bekræft accept
              </button>
            </div>
          </div>
        </div>}
    </>;
}